import { Card, DChip, Icon, Pane, PaneHead } from '@/components/kit';
import {
  REASON_LABEL,
  byActor,
  dayMonth,
  expiryLabel,
  expiryTone,
  useItemHistory,
  type Movement,
  type ShelfItem,
  type ShelfLot,
} from './lib/useStock';

/**
 * The item docked beside the shelf (f1's right pane).
 *
 * THREE QUESTIONS, IN THIS ORDER: how much is there, which box to open first,
 * and where the rest of it went. The middle one is why this pane exists at
 * all — a single number on a shelf row cannot say that six of the thirty-six
 * vials expire in a fortnight, and that is the only part of the figure the
 * stock manager can still do something about.
 *
 * FEFO, not FIFO: the batches are ordered by the date on the box, not by when
 * it arrived, because a later delivery with a shorter date is the one to open.
 * The server sorts them that way; this pane does not re-sort.
 */
export function StockItemPane({
  item,
  branchName,
  canWrite,
  onEditMin,
  onWriteOff,
  onEdit,
  onTransfer,
  onClose,
}: {
  item: ShelfItem;
  branchName: string;
  canWrite: boolean;
  onEditMin: () => void;
  onWriteOff: (lot: ShelfLot, ret?: boolean) => void;
  onEdit?: () => void;
  onTransfer?: () => void;
  onClose: () => void;
}) {
  const { data: history, isLoading } = useItemHistory(item.id);
  const moves = history ?? [];
  /* The receipt that put each lot here — its day and its invoice number, which
     is what the clinic recognises a box by. */
  const receipt = new Map<string, Movement>();
  for (const m of [...moves].reverse()) {
    if (m.reason === 'receipt' && m.lotId) receipt.set(m.lotId, m);
  }
  const onShelf = item.lots.filter((l) => l.quantity > 0);

  return (
    <Pane
      onClose={onClose}
      foot={
        canWrite && (onEdit || onTransfer) ? (
          <>
            {onEdit ? <button type="button" className="btn btn-outline" onClick={onEdit}>Edit definition ▸</button> : null}
            {onTransfer ? <button type="button" className="btn btn-outline" onClick={onTransfer}>Add to transfer</button> : null}
          </>
        ) : undefined
      }
      head={
        <div className="stock-pane-head">
        <PaneHead
          overline={`Item · ${branchName}`}
          title={item.name}
          sub={`${item.unit || item.category || 'item'} · ${
            item.batchTracked ? 'batch-tracked' : 'no batches'
          }`}
          wrapTitle
        />
        </div>
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div className="card" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">In stock</p>
          <p className={`t-heading mt8${item.low ? ' amb' : ''}`}>{item.inStock}</p>
        </div>
        {canWrite ? (
          <button type="button" className="card text-start" style={{ flex: 1, margin: 0 }} onClick={onEditMin}>
            <p className="t-ovl mut">Min</p>
            <p className="t-heading mt8">
              {item.minLevel ?? '—'} <Icon name="pencil" />
            </p>
          </button>
        ) : (
          <div className="card" style={{ flex: 1, margin: 0 }}>
            <p className="t-ovl mut">Min</p>
            <p className="t-heading mt8">{item.minLevel ?? '—'}</p>
          </div>
        )}
        <div className="card" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">≈ / mo</p>
          {/* MEASURED, NOT DECLARED — ninety days of dispensing over three. An
              item nobody has used yet has no rate, and a printed zero would
              read as "we never need this". */}
          <p className="t-heading mt8">{item.perMonth ?? '—'}</p>
          {item.perMonth === null ? (
            <p className="t-cap mut">no dispensing logged in 90 days</p>
          ) : null}
        </div>
      </div>

      {item.minLevel != null && item.perMonth ? (
        <p className="t-cap mut" style={{ marginBottom: 12 }}>
          {item.inStock > 0
            ? `at ≈${item.perMonth}/mo, ${item.inStock} covers ${weeksOf(item.inStock, item.perMonth)}`
            : 'nothing on the shelf — every use of this is a shortage'}
        </p>
      ) : null}

      <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>
        {item.batchTracked ? 'Batches — earliest first (FEFO)' : 'On the shelf'}
      </p>

      {onShelf.length === 0 ? (
        <Card>
          <p className="t-sec mut">
            Nothing is on the shelf. The next receipt against this item opens a new{' '}
            {item.batchTracked ? 'batch' : 'line'}.
          </p>
        </Card>
      ) : (
        onShelf.map((lot) => {
          const tone = expiryTone(lot.expiresOn);
          const rec = lot.id ? receipt.get(lot.id) : undefined;
          return (
            <Card
              key={lot.id}
              style={tone === 'danger' ? { borderColor: 'var(--danger-600)' } : undefined}
            >
              <div className="flex">
                <p className="t-strong grow">
                  {lot.lotCode ? `${lot.lotCode} · ` : ''}
                  {lot.expiresOn ? `exp ${expiryLabel(lot.expiresOn)}` : 'no expiry'}
                </p>
                <p className="t-strong">{lot.quantity}</p>
              </div>
              {rec ? (
                <p className="t-cap mut mt8">
                  received {dayMonth(rec.at)}
                  {rec.reference ? ` · ${rec.reference}` : ''}
                  {rec.actorName ? ` · ${rec.actorName}` : ''}
                </p>
              ) : null}
              {canWrite ? (
                /* THE TWO THINGS WORTH DOING ABOUT A BOX (`g_k6`): take it off
                   the shelf for a reason, or send it back. Both write the
                   movement that says why, so the number never changes silently. */
                <p className="mt8 flex" style={{ gap: 8 }}>
                  <DChip kind={tone === 'danger' ? 'warn' : 'outline'} onClick={() => onWriteOff(lot)}>Write off ▾</DChip>
                  <DChip onClick={() => onWriteOff(lot, true)}>Return to supplier</DChip>
                </p>
              ) : null}
            </Card>
          );
        })
      )}

      <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Movements</p>
      {isLoading ? (
        <p className="t-sec mut">Reading its history…</p>
      ) : moves.length === 0 ? (
        <p className="t-sec mut">Nothing has moved yet.</p>
      ) : (
        moves.slice(0, 12).map((m) => (
          <p key={m.id} className="t-sec sec mt8">
            {dayMonth(m.at)} {m.delta > 0 ? `+${m.delta}` : `−${Math.abs(m.delta)}`} ·{' '}
            {REASON_LABEL[m.reason] ?? m.reason}
            {m.reference ? ` ${m.reference}` : ''}
            {byActor(m.actorName)}
            {m.note ? ` (${m.note})` : ''}
          </p>
        ))
      )}

      <Card className="mt12">
        <p className="t-ovl mut">Sold at the desk</p>
        <p className="t-strong mt8">
          {item.sellable ? 'Sells at the desk' : 'Not sold — consumable only'}
        </p>
        <p className="t-cap mut mt8">
          a stock item becomes sellable in Reception’s catalog, where its price and tax live
        </p>
      </Card>
    </Pane>
  );
}

/** "about five weeks" — the figure the draft-order card reasons with. */
function weeksOf(inStock: number, perMonth: number): string {
  const weeks = Math.round((inStock / perMonth) * 4.3);
  if (weeks <= 0) return 'less than a week';
  if (weeks === 1) return 'about a week';
  if (weeks >= 52) return 'over a year';
  if (weeks > 8) return `about ${Math.round(weeks / 4.3)} months`;
  return `about ${weeks} weeks`;
}
